import React from 'react';
import { CommunicationResponse } from '../../services/faultIntelligenceService';

interface CommunicationStatusCardProps {
  communicationData: CommunicationResponse | null;
  loading?: boolean;
}

const CommunicationStatusCard: React.FC<CommunicationStatusCardProps> = ({
  communicationData,
  loading
}) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border">
        <div className="ai-skeleton h-6 w-48 mb-4"></div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <div className="ai-skeleton h-4 w-32"></div>
              <div className="ai-skeleton h-4 w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Online': return 'text-green-600 bg-green-100';
      case 'Offline': return 'text-red-600 bg-red-100';
      case 'Intermittent': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Online': return '🟢';
      case 'Offline': return '🔴';
      case 'Intermittent': return '🟡';
      default: return '⚪';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Communication Status
        </h3>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-red-600 font-semibold">{communicationData?.failure_count || 0}</span>
            <span className="text-gray-500">failures</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-900 dark:text-white font-semibold">{communicationData?.total_devices || 0}</span>
            <span className="text-gray-500">total devices</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {communicationData?.devices?.filter(d => d.status === 'Online').length || 0}
          </div>
          <div className="text-sm text-green-700 dark:text-green-400">Online</div>
        </div>
        <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <div className="text-2xl font-bold text-red-600">
            {communicationData?.devices?.filter(d => d.status === 'Offline').length || 0}
          </div>
          <div className="text-sm text-red-700 dark:text-red-400">Offline</div>
        </div>
        <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {communicationData?.devices?.filter(d => d.status === 'Intermittent').length || 0}
          </div>
          <div className="text-sm text-yellow-700 dark:text-yellow-400">Intermittent</div>
        </div>
      </div>

      {/* Device List */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {communicationData?.devices?.map((device) => (
          <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-lg">{getStatusIcon(device.status)}</span>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {device.equipment_id}
                </div>
                <div className="text-sm text-gray-500">
                  {device.equipment_type}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(device.status)}`}>
                {device.status}
              </span>
              {device.last_seen_minutes !== null && (
                <div className="text-xs text-gray-500 mt-1">
                  {device.last_seen_minutes}min ago
                </div>
              )}
            </div>
          </div>
        ))}
        
        {(!communicationData?.devices || communicationData.devices.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No communication data available
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationStatusCard;